import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getAuth, 
  signInWithPopup, 
  GoogleAuthProvider, 
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink
} from 'firebase/auth';
import { trackEvent } from '../components/Analytics';
import assistantAvatar from '../assets/images/Odysseus.jpg';
import googleIcon from '../assets/icons/google_icon.png';

const LoadingSpinner = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

const AuthComponent = () => {
  const [loading, setLoading] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState(null);
  const [emailForSignIn, setEmailForSignIn] = useState('');
  const [linkSent, setLinkSent] = useState(false);
  const navigate = useNavigate();
  const auth = getAuth();
  const provider = new GoogleAuthProvider();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const success = await createUserDocument(user);
        if (success) {
          trackEvent('login_success', { method: 'auto' });
          navigate('/app/checkout');
        }
      }
      setLoading(false);
    });

    if (isSignInWithEmailLink(auth, window.location.href)) {
      let email = window.localStorage.getItem('emailForSignIn');
      if (!email) {
        email = window.prompt('Please provide your email for confirmation');
      }
      
      signInWithEmailLink(auth, email, window.location.href)
        .then(async (result) => {
          window.localStorage.removeItem('emailForSignIn');
          const success = await createUserDocument(result.user);
          if (success) {
            trackEvent('login_success', { method: 'email_link' });
            navigate('/app/checkout');
          }
        })
        .catch((error) => {
          console.error('Email link sign in error:', error);
          setError('Failed to sign in with email link. Please try again.');
        });
    }

    return () => unsubscribe();
  }, [auth, navigate]);

  const handleGoogleSignIn = async () => {
    try {
      setError(null);
      setSigningIn(true);
      const result = await signInWithPopup(auth, provider);
      const success = await createUserDocument(result.user);
      if (success) {
        trackEvent('login_success', { method: 'google' });
        navigate('/app/checkout');
      }
    } catch (error) {
      setError('Google sign in failed. Please try again.');
      trackEvent('login_error', { method: 'google', error: error.code });
      console.error('Google sign-in error:', error);
    } finally {
      setSigningIn(false);
    }
  };

  const handleEmailLinkSignIn = async (e) => {
    e.preventDefault();
    if (!emailForSignIn) {
      setError('Please enter your email address');
      return;
    }

    try {
      const actionCodeSettings = {
        url: 'https://app.kneoai.com/auth',
        handleCodeInApp: true
      };

      await sendSignInLinkToEmail(auth, emailForSignIn, actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', emailForSignIn);
      setLinkSent(true);
      trackEvent('email_link_sent', { email: emailForSignIn });
    } catch (error) {
      console.error('Error sending email link:', error);
      setError('Failed to send sign-in link. Please try again.');
    }
  };

  const createUserDocument = async (user) => {
    if (!user) return false;
    try {
      const baseUrl = 'https://ftea-backend-321105010733.europe-west9.run.app';
      const endpoint = `/api/profile/user_profiles/${user.uid}`;
  
      const checkResponse = await fetch(`${baseUrl}${endpoint}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
  
      if (checkResponse.status === 404) {
        const userData = {
          email: user.email,
          displayName: user.displayName || user.email,  // camelCase to match backend
          photoURL: user.photoURL || null               // camelCase to match backend
        };
        
        console.log('Sending profile data to backend:', userData);
        
        const createResponse = await fetch(`${baseUrl}${endpoint}`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(userData)
        });
  
        if (!createResponse.ok) {
          const errorData = await createResponse.text();
          console.error('Profile creation failed:', errorData);
          throw new Error(`Failed to create user profile: ${createResponse.status}`);
        }
  
        const newProfile = await createResponse.json();
        console.log('New profile created:', newProfile);
        return true;
      } else if (!checkResponse.ok) {
        throw new Error(`Server error: ${checkResponse.status}`);
      } else {
        const existingProfile = await checkResponse.json();
        console.log('Using existing profile:', existingProfile);
        return true;
      }
    } catch (error) {
      console.error('Profile creation error:', error);
      setError(`Failed to process user profile: ${error.message}`);
      return false;
    }
  };

  if (loading || signingIn) {
    return <LoadingSpinner />;
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-cover bg-center relative"
      style={{ backgroundImage: `url(${assistantAvatar})` }}>
      <div className="p-8 bg-white bg-opacity-80 rounded-lg shadow-md max-w-md w-full">
        <h1 className="text-3xl font-bold text-center mb-8">Welcome to Odysseus</h1>
        
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {linkSent ? (
          <div className="text-center mb-4 p-4 bg-green-100 text-green-700 rounded-lg">
            Check your email! We've sent you a sign-in link.
          </div>
        ) : (
          <>
            <form onSubmit={handleEmailLinkSignIn} className="space-y-4 mb-6">
              <input
                type="email"
                placeholder="Sign in with email link"
                value={emailForSignIn}
                onChange={(e) => setEmailForSignIn(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Send Sign In Link
              </button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white bg-opacity-80 text-gray-500">Or</span>
              </div>
            </div>
            
            <button
              onClick={handleGoogleSignIn}
              disabled={signingIn}
              className="flex items-center justify-center w-full px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <img src={googleIcon} alt="Google" className="w-6 h-6 mr-2" />
              Sign in with Google
            </button>
            
            <div className="mt-6 text-center text-sm text-gray-500">
              <p>By signing in, you agree to our</p>
              <div className="mt-1">
                <a href="/legal/termsofservice" className="text-blue-600 hover:underline">Terms of Service</a>
                {' & '}
                <a href="/legal/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthComponent;